import {
  ActivityInboxType,
  EnvConfig,
  ISSUE_MESSAGE_PAGE_COUNT,
  Issue,
  MediaType,
  Message,
  useFindIssueMessages,
  useNewIssueMessageSubscription,
  useUnreadActivityInboxSubscription,
} from '@gripp/shared-logic';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { FlatList, StyleSheet, View } from 'react-native';
import { ActivityIndicator, Text } from 'react-native-paper';
import { Layout } from '../../styles';
import { Colors } from '../../themes/colors';
import {
  ImageGalleryImage,
  ImageGalleryModal,
} from '../crop-circle/Components/ImageGallery';
import { DocumentBIcon } from '../crop-circle/icons';
import { spacing, tokens } from '../crop-circle/tokens';
import { BottomComponentBar } from '../navigation';
import { IssueMessage } from './issueMessage';

export type IssueMessagesProps = {
  issue: Issue;
  onMessagesCountChange?: (messagesCount: number) => void;
  mediaUrl: string;
  inbox: any[];
  tsUpdate?: string;
  setAsRead: (
    issueId: string,
    inboxes: any[],
    invalidate: boolean
  ) => Promise<void>;
  /** Forwarded to each message's overflow "Call" action (gated upstream). */
  onCall?: (actor: { id: string; name: string }) => void;
  /**
   * Side Chat (plan 0022): forwarded to each message's overflow "Side chat"
   * action + "X replies" pill (gated upstream). Undefined hides both.
   */
  onOpenSideChat?: (message: Message) => void;
  /**
   * Side Chat (plan 0022): per-origin summaries keyed by message id, used to
   * render each message's "X replies · Side Chat" pill.
   */
  sideChatSummaries?: Map<string, { exists: boolean; replyCount: number }>;
  /**
   * End-to-end calling demo (plan 0021 addendum). Forwarded to the first
   * message's "Call in progress · Join" pill (gated upstream).
   */
  callInProgress?: boolean;
  /** Opens/joins the Activity's group call. Paired with `callInProgress`. */
  onJoinCall?: () => void;
};

export type IssueMessagesRef = {
  addNewMessage: (message: Message) => void;
};

export const IssueMessages = forwardRef<IssueMessagesRef, IssueMessagesProps>(
  (props, ref) => {
    const [messages, setMessages] = useState<Message[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const {
      data,
      isLoading,
      hasNextPage,
      isFetchingNextPage,
      fetchNextPage,
      refetch,
    } = useFindIssueMessages(props.issue.id);
    // if a user goes back and then revisits this page, data might be cached and we could already have more than 1 page
    const [page, setPage] = useState(data?.pages?.length || 0);
    const initialPages = data?.pages?.length || 0;

    const [galleryVisible, setGalleryVisible] = useState(false);
    const [galleryInitialIndex, setGalleryInitialIndex] = useState(0);

    // Side Chat (plan 0022 §C) — MANDATORY main-thread exclusion. There is no
    // safe server-side null filter (`parentMessage: { eq: null }` crashes
    // Graphback's Mongo query builder — see useFindIssueMessages), so the ONLY
    // thing keeping side-chat replies out of the main thread is this client-side
    // filter. Drop any message that is parented to another (belongs to a side
    // chat). The subscription callback below applies the same drop for live
    // replies.
    const visibleMessages = useMemo(
      () => messages.filter((message) => !message.parentMessage),
      [messages]
    );

    const allGalleryImages = useMemo<ImageGalleryImage[]>(() => {
      return visibleMessages.flatMap((message) =>
        (message.media ?? [])
          .filter((m) => m.type === MediaType.Image)
          .map((m) => ({
            id: m.id,
            url: `${EnvConfig.mediaUrl}/images/${m.id}/original.${m.extension}`,
            name: m.name,
          }))
      );
    }, [visibleMessages]);

    const handleImagePress = (mediaId: string) => {
      const idx = allGalleryImages.findIndex((img) => img.id === mediaId);
      if (idx >= 0) {
        setGalleryInitialIndex(idx);
        setGalleryVisible(true);
      }
    };

    useNewIssueMessageSubscription(props.issue.id, (response) => {
      const incoming = response.data.newIssueMessage;
      // Side Chat (plan 0022 §C): a live reply parented to another message
      // belongs to a side chat, not the main thread — drop it here (the
      // subscription is issue-scoped and would otherwise leak it into the list).
      if (incoming?.parentMessage) {
        return Promise.resolve();
      }
      addNewMessage({ ...incoming, isNew: true });
      return Promise.resolve();
    });

    useUnreadActivityInboxSubscription(async (inboxItem) => {
      if (inboxItem.data.id === props.issue.id) {
        await props.setAsRead(props.issue.id, [inboxItem], false);
      }
    });

    const addNewMessage = (message: Message) => {
      setMessages((prevMessages: Message[]) => [message, ...prevMessages]);
    };

    useImperativeHandle(ref, () => ({
      addNewMessage: addNewMessage,
    }));

    useEffect(() => {
      if (!props.tsUpdate) return;

      refetch();
    }, [props.tsUpdate, refetch]);

    useEffect(() => {
      if (
        isLoading ||
        isFetchingNextPage ||
        !data ||
        !data.pages ||
        data.pages.length === 0
      )
        return;

      if (page > 0 && page > initialPages) {
        setMessages((prevMessages) => [
          ...prevMessages,
          ...data.pages[page].items,
        ]);
      } else {
        setTotalCount(data.pages[0].count);
        props.onMessagesCountChange &&
          props.onMessagesCountChange(data.pages[0].count);
        setMessages(data.pages.flatMap((page) => page?.items));
      }
    }, [data, initialPages, isFetchingNextPage, isLoading, page]);

    const onEndReached = () => {
      if (!totalCount || totalCount <= ISSUE_MESSAGE_PAGE_COUNT) return;
      if (!isFetchingNextPage && hasNextPage) {
        setPage(page + 1);
        fetchNextPage();
      }
    };

    const isUnread = (message: any) => {
      if (!props.inbox) {
        return false;
      }

      return (
        message.isNew ||
        props.inbox.some(
          (i) =>
            i.type === ActivityInboxType.IssueMessageCreated &&
            i.data.messageId === message.id
        )
      );
    };
    return (
      <View style={[styles.container]}>
        {isFetchingNextPage && (
          <View>
            <ActivityIndicator
              size="large"
              animating={isLoading || isFetchingNextPage}
            />
          </View>
        )}
        <FlatList
          refreshing={isFetchingNextPage}
          data={visibleMessages}
          extraData={visibleMessages}
          inverted={true}
          onEndReached={onEndReached}
          // Dragging the thread dismisses the keyboard, which blurs the
          // composer; the composer then collapses back to its pill if the
          // draft is empty (see issueFooterMobile's onInputBlur). This is what
          // makes "pull down / scroll back to close the input" work without
          // the list needing to know the composer exists.
          //
          // 'on-drag' rather than iOS's 'interactive': the list is inverted,
          // and interactive dismissal tracks the gesture in the flipped
          // coordinate space, so the keyboard follows the finger the wrong way.
          keyboardDismissMode="on-drag"
          renderItem={({ item, index }) => (
            <IssueMessage
              key={item.id}
              message={item}
              mediaUrl={props.mediaUrl}
              isUnread={isUnread(item)}
              issue={props.issue}
              isFirstMessage={index === visibleMessages.length - 1}
              onImagePress={handleImagePress}
              onCall={props.onCall}
              onOpenSideChat={props.onOpenSideChat}
              sideChatSummary={props.sideChatSummaries?.get(item.id)}
              callInProgress={props.callInProgress}
              onJoinCall={props.onJoinCall}
            />
          )}
        />

        {allGalleryImages.length > 0 && (
          <ImageGalleryModal
            visible={galleryVisible}
            onClose={() => setGalleryVisible(false)}
            images={allGalleryImages}
            initialIndex={galleryInitialIndex}
            renderHeader={(activeImage) => (
              <BottomComponentBar
                left={
                  <View style={styles.leftNode}>
                    <DocumentBIcon />
                    <Text
                      style={tokens.typography.body.mediumEmphasised}
                      numberOfLines={1}
                    >
                      {activeImage?.name && activeImage.name.length > 25
                        ? `${activeImage.name.slice(0, 25)}...`
                        : activeImage?.name}
                    </Text>
                  </View>
                }
                backButton={undefined}
              />
            )}
            contentStyle={{ maxHeight: 500 }}
          />
        )}
      </View>
    );
  }
);

const styles = StyleSheet.create({
  container: {
    ...Layout.container,
    backgroundColor: Colors.role.canvas,
  },
  leftNode: {
    marginLeft: spacing.md,
    marginTop: spacing.md,
    marginRight: 'auto',
    display: 'flex',
    flexDirection: 'row',
    gap: 4,
    alignItems: 'center',
  },
});
